import { extractPermissionsFromToken, extractRolesFromToken } from "@/shared/utils/jwtUtils";
import { create } from "zustand";

export interface AuthUser {
  id: string;
  username: string;
  firstName: string;
  otherNames: string;
  email: string;
  phoneNumber: string | null;
  isActive: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  mustChangePassword: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  roles?: string[];
  agronomistRegions?: string[];
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  permissions: string[];
  isAuthenticated: boolean;
  /** ISO timestamp of access-token expiry; drives the silent-refresh scheduler. */
  expiresAt: string | null;

  setAuth: (user: AuthUser, token: string, permissions: string[], refreshToken?: string, expiresAt?: string) => void;
  logout: () => void;
  setUser: (user: AuthUser) => void;
  hasPermission: (permission: string) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  refreshToken: null,
  permissions: [],
  isAuthenticated: false,
  expiresAt: null,

  setAuth: (user, token, permissions, refreshToken, expiresAt) => {

    const permsToStore = permissions.length > 0
      ? permissions
      : extractPermissionsFromToken(token);

    localStorage.setItem("accessToken", token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("permissions", JSON.stringify(permsToStore));
    if (expiresAt) {
      localStorage.setItem("accessTokenExpiresAt", expiresAt);
    }
    if (refreshToken) {
      localStorage.setItem("lastSessionRefreshAt", String(Date.now()));
      // Other tabs use this as the marker that the entire rotated session is stored.
      localStorage.setItem("refreshToken", refreshToken);
    }
    set({
      user,
      token,
      refreshToken: refreshToken ?? get().refreshToken,
      permissions: permsToStore,
      isAuthenticated: true,
      expiresAt: expiresAt ?? get().expiresAt,
    });
  },

  logout: () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("accessTokenExpiresAt");
    localStorage.removeItem("lastSessionRefreshAt");
    set({
      user: null,
      token: null,
      refreshToken: null,
      permissions: [],
      isAuthenticated: false,
      expiresAt: null,
    });
  },

  setUser: (user) => set({ user }),

  hasPermission: (permission) =>
    get().permissions.includes(permission),

  hasAllPermissions: (perms) =>
    perms.every((p) => get().permissions.includes(p)),

  hasAnyPermission: (perms) =>
    perms.some((p) => get().permissions.includes(p)),

  initializeAuth: () => {
    const token = localStorage.getItem("accessToken");
    const userJson = localStorage.getItem("user");
    const permissionsJson = localStorage.getItem("permissions");
    const refreshToken = localStorage.getItem("refreshToken");
    const expiresAt = localStorage.getItem("accessTokenExpiresAt");

    if (token && userJson) {
      try {
        const user = JSON.parse(userJson);
        let permissions = permissionsJson ? JSON.parse(permissionsJson) : [];

        if (permissions.length === 0) {
          permissions = extractPermissionsFromToken(token);
        }

        // Re-derive roles from the token so sessions cached before the
        // role-from-JWT fix don't keep the old hardcoded ['user'] label.
        const tokenRoles = extractRolesFromToken(token);
        if (tokenRoles.length > 0) {
          user.roles = tokenRoles;
        }

        set({ user, token, refreshToken, permissions, isAuthenticated: true, expiresAt });
      } catch (error) {
        console.error("Failed to parse auth data from localStorage:", error);
        set({
          user: null,
          token: null,
          refreshToken: null,
          permissions: [],
          isAuthenticated: false
        });
      }
    }
  },
}));
